import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Tag, Layers, Users2, Clock, ExternalLink, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { PROJECT_TYPES, PROJECT_STAGES, PROFILE_TYPES } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function ProjectDetail() {
  const { slug } = useParams();
  const { lang, L, t } = useLang();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => { api.get(`/projects/${slug}`).then(({ data }) => setO(data)).catch(() => setError(true)); }, [slug]);
  useSeo({ title: o ? L(o, "title") : "", description: o ? (L(o, "summary") || L(o, "need")) : "", lang, image: o?.cover_image, type: "article" });

  if (error) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/60">404</div>;
  if (!o) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/40">…</div>;
  const v = o.verification || {};

  const Row = ({ icon: Icon, label, value }) => value ? (
    <div className="flex items-start gap-2 text-sm"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-champagne" /><span className="text-anthracite/50">{label} :</span><span className="text-anthracite/80">{value}</span></div>
  ) : null;

  return (
    <article className="mx-auto max-w-4xl px-6 py-12 lg:px-10">
      <button onClick={() => navigate("/projets")} className="mb-8 inline-flex items-center gap-2 text-sm text-anthracite/60 hover:text-champagne"><ArrowLeft className="h-4 w-4" /> {lang === "fr" ? "Tous les projets" : "All projects"}</button>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-[0.2em] text-champagne">{PROJECT_TYPES.find((x) => x.value === o.project_type)?.[lang]}</span>
        <VerifiedBadge verified={o.rst_verified} size="lg" />
      </div>

      <h1 className="mb-4 font-serif text-4xl leading-tight tracking-tight text-anthracite sm:text-5xl">{L(o, "title")}</h1>
      {L(o, "summary") && <p className="mb-8 text-lg leading-relaxed text-anthracite/70">{L(o, "summary")}</p>}

      {o.cover_image && <div className="mb-10 aspect-[16/9] overflow-hidden bg-sand"><img src={o.cover_image} alt="" className="h-full w-full object-cover" /></div>}

      <div className="mb-8 grid gap-3 border-y border-tertiary py-5 sm:grid-cols-2">
        <Row icon={Tag} label={t("sector")} value={o.sector} />
        <Row icon={MapPin} label={lang === "fr" ? "Pays / marché" : "Country / market"} value={[o.region, o.country].filter(Boolean).join(", ")} />
        <Row icon={Layers} label={lang === "fr" ? "Stade" : "Stage"} value={PROJECT_STAGES.find((s) => s.value === o.stage)?.[lang]} />
        <Row icon={Sparkles} label={lang === "fr" ? "Montant recherché" : "Amount sought"} value={o.amount_text} />
        <Row icon={Users2} label={lang === "fr" ? "Profil recherché" : "Profile sought"} value={(o.profile_sought || []).map((p) => PROFILE_TYPES.find((x) => x.value === p)?.[lang]).filter(Boolean).join(", ")} />
      </div>

      {L(o, "description") && <Section title={lang === "fr" ? "Description" : "Description"} body={L(o, "description")} />}
      {L(o, "need") && <Section title={lang === "fr" ? "Besoin" : "Need"} body={L(o, "need")} />}
      {L(o, "offer") && <Section title={lang === "fr" ? "Offre proposée" : "Offer"} body={L(o, "offer")} />}

      <p className="mb-10 text-xs text-anthracite/50" data-testid="rst-trust-note">{lang === "fr" ? "Les fiches publiées ont fait l'objet d'une validation interne par RSTI. Cette validation réduit l'incertitude mais ne constitue jamais une garantie d'absence de risque. La mise en relation reste encadrée par RSTI." : "Published records undergo internal validation by RSTI. This reduces uncertainty but is never a guarantee of the absence of risk. Introductions remain facilitated by RSTI."}</p>

      <div className="flex flex-col gap-3 border-t border-tertiary pt-8 sm:flex-row">
        <Link to={`/connect?category=projects&ref=${encodeURIComponent(L(o, "title"))}`} data-testid="cta-interested" className="flex-1 bg-anthracite px-6 py-4 text-center text-sm font-medium text-ivory transition-colors hover:bg-black">{lang === "fr" ? "Je suis intéressé" : "I'm interested"}</Link>
        <Link to={`/connect?category=projects&ref=${encodeURIComponent(L(o, "title"))}`} data-testid="cta-accompanied" className="flex-1 border border-anthracite px-6 py-4 text-center text-sm font-medium text-anthracite transition-colors hover:bg-anthracite hover:text-ivory">{lang === "fr" ? "Être mis en relation par RSTI" : "Get introduced by RSTI"}</Link>
      </div>
    </article>
  );
}

function Section({ title, body }) {
  return (
    <div className="mb-8">
      <h3 className="mb-2 font-serif text-xl text-anthracite">{title}</h3>
      <p className="whitespace-pre-line text-base leading-relaxed text-anthracite/80">{body}</p>
    </div>
  );
}
